import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useSEO from "@/lib/useSEO";

export default function AdminLogin() {
  useSEO("Admin Sign In | Fleece The Regime", "Back office sign in.", true);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("password"); // password | code
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (step === "password") {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.otp_required) {
          setStep("code");
          return;
        }
        localStorage.setItem("ut_admin_token", data.token);
        navigate("/admin");
      } else {
        const { data } = await api.post("/auth/login/verify", { email, code });
        localStorage.setItem("ut_admin_token", data.token);
        navigate("/admin");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="pt-16 min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md border-2 border-black bg-background p-8 shadow-hard-lg">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary">Back office</p>
        <h1 className="mt-3 font-display uppercase text-4xl tracking-tighter">Who goes there?</h1>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block mb-2 font-mono text-xs font-bold uppercase tracking-[0.2em]">Email</label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="w-full border-2 border-black bg-background px-4 py-3 font-mono text-sm outline-none focus:shadow-hard transition-shadow"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block mb-2 font-mono text-xs font-bold uppercase tracking-[0.2em]">Password</label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="w-full border-2 border-black bg-background px-4 py-3 font-mono text-sm outline-none focus:shadow-hard transition-shadow"
            />
          </div>
          {step === "code" && (
            <div>
              <label htmlFor="admin-otp" className="block mb-2 font-mono text-xs font-bold uppercase tracking-[0.2em]">One-time code</label>
              <input
                id="admin-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                data-testid="admin-otp-input"
                placeholder="6-digit code"
                className="w-full border-2 border-black bg-background px-4 py-3 font-mono text-sm tracking-[0.5em] outline-none focus:shadow-hard transition-shadow"
              />
              <p className="mt-2 font-mono text-[11px] text-black/60">Password accepted. Check the store owner's email for the code — it expires in 10 minutes.</p>
            </div>
          )}
          {error && (
            <p className="border-2 border-primary bg-primary/10 px-4 py-3 font-mono text-xs font-bold text-primary" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full border-2 border-black bg-black py-4 font-display uppercase text-xl text-background shadow-hard btn-press hover:bg-primary disabled:opacity-50"
          >
            {loading ? "Checking papers…" : step === "code" ? "Verify code" : "Let me in"}
          </button>
          {step === "code" && (
            <button
              type="button"
              data-testid="admin-otp-back"
              onClick={() => { setStep("password"); setCode(""); setError(""); }}
              className="w-full border-2 border-black py-3 font-mono text-xs font-bold uppercase hover:bg-accent transition-colors"
            >
              Back to password
            </button>
          )}
        </form>
      </div>
    </main>
  );
}
